import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestHeaders,
  type InternalAxiosRequestConfig,
} from 'axios'
import { logWithBadge } from '@/shared/utils/logging'

// Long enough that a signed-out window cannot loop on the re-authentication endpoint.
const SESSION_REPAIR_COOLDOWN_MILLISECONDS = 30_000

type RepairableRequestConfig = InternalAxiosRequestConfig & { sessionRepairAttempted?: boolean }

let sessionRepairInFlight: Promise<boolean> | null = null
let lastSessionRepairAtMilliseconds = 0

function getCSRFToken() {
  const tokenEl = document.querySelector<HTMLMetaElement>('[name="csrf-token"]')
  if (tokenEl) {
    return tokenEl.content
  }
  return null
}

// Cache the token in memory so we don’t query the DOM on every request
const csrfToken = getCSRFToken()

const api: AxiosInstance = axios.create({
  baseURL: '/',
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

api.interceptors.request.use((config) => {
  const method = (config.method ?? 'get').toUpperCase()
  const needsCsrf = !['GET', 'HEAD', 'OPTIONS'].includes(method)

  const headers = config.headers as Record<string, string>

  // Default content type for JSON bodies
  if (!headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
  }

  if (needsCsrf && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken
  }

  config.headers = headers as AxiosRequestHeaders
  return config
})

// A sign-in rotates the session's CSRF token, so a window that outlives one holds a token the
// server rejects: mutations 422, and once the session lapses reads 401, until the window reloads.
// The main process recovers this for navigations that redirect to login; an XHR gets no redirect.
// Re-authenticating writes a fresh XSRF-TOKEN cookie, so the replay needs no new token of its own.
function repairSession(authenticateSession: () => Promise<void>): Promise<boolean> {
  if (sessionRepairInFlight) return sessionRepairInFlight

  if (Date.now() - lastSessionRepairAtMilliseconds < SESSION_REPAIR_COOLDOWN_MILLISECONDS) {
    logWithBadge('SessionRepair', 'still cooling down, leaving the request failed')
    return Promise.resolve(false)
  }

  sessionRepairInFlight = authenticateSession()
    .then(() => true)
    .catch((error: unknown) => {
      logWithBadge('SessionRepair', 're-authentication failed', error)
      return false
    })
    .finally(() => {
      lastSessionRepairAtMilliseconds = Date.now()
      sessionRepairInFlight = null
    })

  return sessionRepairInFlight
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RepairableRequestConfig | undefined
    const status = error.response?.status
    // Only the desktop app can re-authenticate its own session; the web app has nothing to call.
    const authenticateSession = window.electronAPI?.authenticateSession

    if (!config || !authenticateSession || config.sessionRepairAttempted) return Promise.reject(error)
    if (status !== 401 && status !== 422) return Promise.reject(error)

    logWithBadge('SessionRepair', 'repairing session', { method: config.method, status, url: config.url })
    if (!(await repairSession(authenticateSession))) return Promise.reject(error)

    config.sessionRepairAttempted = true
    logWithBadge('SessionRepair', 'session repaired, replaying request', { url: config.url })
    return api.request(config)
  },
)

export default api
